import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { CardCarousel } from '@iheartjane/menu';
import { Flex, JaneGoldIcon, Link } from '@iheartjane/reefer';
import { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import type {
  MenuResponse,
  StoreSpecialsResponse,
} from '@jane/business-admin/data-access';
import {
  useCurrentStoreSpecials,
  useFetchAllStoreSpecials,
  useStoreMenuPaginated,
} from '@jane/business-admin/data-access';
import {
  useMenuRowSdkProducts,
  useMenuRowType,
} from '@jane/business-admin/hooks';
import type { EmbeddedMenuRow, MenuRow } from '@jane/business-admin/types';
import type { AlgoliaProduct } from '@jane/search/types';
import { useStoreHasGoldConfig } from '@jane/shared/hooks';

import { ListItem } from './ListItem';
import { EmbedCodeButton } from './ListItemHeaders';
import { PreviewCardsListItem } from './PreviewCardsListItem';
import { RecommendedRowListItem } from './RecommendedRowListItem';
import { SpecialsListItem } from './SpecialsListItem';
import { CARD_HEIGHT, CARD_WIDTH, getRowLabel } from './utils';

const LoadingCarousel = () => {
  const eightItems = new Array(8).fill(null);
  return (
    <>
      {eightItems.map((_, index) => {
        return (
          <li style={{ marginBottom: 20 }} key={`loading-item-${index}`}>
            <CardCarousel
              name="Loading..."
              mode="ideal-width"
              cardWidth={CARD_WIDTH}
              cardHeight={CARD_HEIGHT}
              cardWidthMobile={212}
              cardHeightMobile={328}
              isLoading={true}
            >
              <CardCarousel.Card>
                <p>Loading...</p>
              </CardCarousel.Card>
            </CardCarousel>
          </li>
        );
      })}
    </>
  );
};

interface Props {
  onOpenEmbeddedRowModal: (
    menuRow: MenuRow,
    row?: EmbeddedMenuRow | null
  ) => void;
  onOpenMenuRowModal: (row: MenuRow, hasEmbeddedRow?: boolean) => void;
  showEmbeddedMenuRows: boolean;
  storeId: string;
}
export const MenuRowList = ({
  onOpenEmbeddedRowModal,
  onOpenMenuRowModal,
  showEmbeddedMenuRows = false,
  storeId,
}: Props) => {
  const {
    data: paginatedData,
    fetchNextPage,
    hasNextPage,
    isFetching: menuFetching,
    isFetchingNextPage,
  } = useStoreMenuPaginated(storeId, 10);

  const { ref: loaderRef } = useInView({
    onChange: (inView) => {
      if (inView && hasNextPage) {
        fetchNextPage();
      }
    },
    rootMargin: '100px',
    skip: isFetchingNextPage,
  });
  const showGold = useStoreHasGoldConfig(storeId);
  const { getRowType } = useMenuRowType();
  const { data: storeSpecials, isFetching: specialsFetching } =
    useFetchAllStoreSpecials(storeId);
  const { data: currentStoreSpecials, isFetching: currentSpecialsFetching } =
    useCurrentStoreSpecials(storeId, 0, 10);

  const firstPageData = paginatedData?.pages?.[0];

  const menuRows = useMemo(() => {
    if (!paginatedData?.pages) {
      return [];
    }

    const allRows = paginatedData.pages.flatMap((page) => page.menu_rows);

    const rowsToRender = allRows.filter((rowData) => {
      if (rowData.is_category) return false;
      if (!showGold && rowData.custom_display_name === 'Jane Gold rewards') {
        return false;
      }
      return true;
    });

    return rowsToRender;
  }, [paginatedData, showGold]);

  const { getProductsForRow, isLoading: sdkLoading } = useMenuRowSdkProducts(
    menuRows,
    storeId
  );

  const isFetching = useMemo(() => {
    return (
      menuFetching || sdkLoading || specialsFetching || currentSpecialsFetching
    );
  }, [menuFetching, sdkLoading, specialsFetching, currentSpecialsFetching]);

  const magicRowLabelMap = useMemo(() => {
    const variants: string[] = ['Flower', 'Edibles', 'Vapes'];

    const map = new Map<number, string>();
    let variantIndex: number = 0;

    menuRows.forEach((row) => {
      if (getRowType(row) === 'magic_row' && row.id !== undefined) {
        map.set(row.id, variants[variantIndex] + ' for you');
        variantIndex++;
      }
    });

    return map;
  }, [menuRows, getRowType]);

  return (
    <Flex pb={24} pt={8}>
      <ol style={{ width: '100%' }}>
        {isFetching && menuRows.length === 0 ? (
          <LoadingCarousel />
        ) : (
          <>
            {menuRows.map((row, index) => {
              return (
                <li key={`${row.id}-${index}`}>
                  <ListItems
                    currentStoreSpecials={currentStoreSpecials}
                    embeddedMenuRow={row.embedded_menu_row}
                    menuData={firstPageData}
                    menuRow={row}
                    onOpenEmbeddedRowModal={onOpenEmbeddedRowModal}
                    onOpenMenuRowModal={onOpenMenuRowModal}
                    rowHelperText={
                      getRowType(row) === 'magic_row' && !!row.id
                        ? magicRowLabelMap.get(row.id)
                        : undefined
                    }
                    getProductsForRow={getProductsForRow}
                    showEmbeddedMenuRows={showEmbeddedMenuRows}
                    storeId={storeId}
                    storeSpecials={storeSpecials}
                  />
                </li>
              );
            })}
            {hasNextPage && (
              <div ref={loaderRef} style={{ padding: '20px 0' }}>
                <LoadingCarousel />
              </div>
            )}
          </>
        )}
      </ol>
    </Flex>
  );
};

interface ListItemProps extends Props {
  currentStoreSpecials?: StoreSpecialsResponse;
  embeddedMenuRow?: EmbeddedMenuRow | null;
  getProductsForRow: (
    rowKey: string
  ) => SmartSortProduct<AlgoliaProduct>[] | undefined;
  menuData?: MenuResponse;
  menuRow: MenuRow;
  rowHelperText?: string;
  showEmbeddedMenuRows: boolean;
  storeSpecials?: StoreSpecialsResponse;
}

// TODO(comm2EmbeddedDealRows): Combine into one array when feature is fully released
const EMBEDDABLE_ROWS = ['custom', 'magic_row', 'best_selling', 'sale'];
const EMBEDDABLE_DEAL_ROWS = ['specials', 'brand_specials'];

const ListItems = ({
  currentStoreSpecials,
  embeddedMenuRow,
  menuData,
  menuRow,
  onOpenEmbeddedRowModal,
  onOpenMenuRowModal,
  rowHelperText,
  getProductsForRow,
  showEmbeddedMenuRows = false,
  storeId,
  storeSpecials,
}: ListItemProps) => {
  const thematicRows = useFlag(FLAGS.comm3MultipleThematicRows);
  const embeddedDealRows = useFlag(FLAGS.comm2EmbeddedDealRows);

  const { getRowType, useMenuRowType: useMenuRowTypeFlag } = useMenuRowType();

  const menuRowTypeValue = getRowType(menuRow);

  const showEmbedButton = useMemo(() => {
    if (!menuRowTypeValue) return false;
    if (!showEmbeddedMenuRows) return false;

    if (embeddedDealRows && EMBEDDABLE_DEAL_ROWS.includes(menuRowTypeValue))
      return true;

    return EMBEDDABLE_ROWS.includes(menuRowTypeValue);
  }, [menuRowTypeValue, showEmbeddedMenuRows, embeddedDealRows]);

  switch (menuRowTypeValue) {
    case 'specials':
      return (
        <SpecialsListItem
          complianceLanguageSettings={
            menuData?.filters_and_labels?.compliance_language_settings_labels
          }
          currentStoreSpecials={currentStoreSpecials?.specials || []}
          embeddedMenuRow={embeddedMenuRow}
          openEmbedRowModal={onOpenEmbeddedRowModal}
          openMenuRowModal={onOpenMenuRowModal}
          row={menuRow}
          showEmbedButton={showEmbedButton}
        />
      );
    case 'buy_again_row':
      return (
        <PreviewCardsListItem
          cardText="Purchased product"
          row={menuRow}
          rowHelperText="Each logged in customer will see products they have previously purchased."
        />
      );
    case 'magic_row':
      return (
        <RecommendedRowListItem
          embeddedMenuRow={embeddedMenuRow}
          menuRow={menuRow}
          openEmbedRowModal={onOpenEmbeddedRowModal}
          storeId={storeId}
          rowHelperText={rowHelperText}
          showEmbedButton={showEmbedButton}
        />
      );
    case 'brand_specials':
      return (
        <PreviewCardsListItem
          afterHeader={
            showEmbedButton ? (
              <EmbedCodeButton
                hasEmbedCode={!!embeddedMenuRow}
                onClick={() => onOpenEmbeddedRowModal(menuRow, embeddedMenuRow)}
              />
            ) : undefined
          }
          cardIcon={() => (
            <JaneGoldIcon color={menuRow.enabled ? undefined : 'grays-light'} />
          )}
          cardText="Eligible reward"
          row={menuRow}
          rowHelperText={
            <>
              Each customer will see Jane Gold rewards based on their deal
              eligibility.{' '}
              <Link
                color="grays-mid"
                href="https://docs.iheartjane.com/partner-success/i-love-gooooold"
              >
                Learn more about Jane Gold
              </Link>
            </>
          }
          wide
        />
      );
    default: {
      // SDK products are only fetched for jane-defined rows (best_selling,
      // sale), so the row type string is sufficient as a lookup key.
      const rowKey = menuRowTypeValue ?? '';
      return (
        <ListItem
          allStoreSpecials={storeSpecials?.specials}
          embeddedMenuRow={embeddedMenuRow}
          openEmbedRowModal={onOpenEmbeddedRowModal}
          products={getProductsForRow(rowKey)}
          row={menuRow}
          rowLabel={getRowLabel(
            menuRow,
            menuData?.filters_and_labels.custom_labels,
            useMenuRowTypeFlag,
            thematicRows
          )}
          openMenuRowModal={onOpenMenuRowModal}
          showEmbedButton={showEmbedButton}
        />
      );
    }
  }
};
